import axios from "axios";
import { store } from "Store/mainStore";

class OnboardingService {
    // ✅ Centralized properties - single source of truth
    private DEFAULT_USER_PROPERTIES = [
        "users.id",
        "users.name",
        "users.email",
        "user_roles.role",
        "users.type",
        "users.last_login_time",
        "users.login_expiration",
        "users.createdAt",

    ];

    /**
     * Get user state from Redux store
     */
    getState() {
        const reduxtStore = store.getState();
        const email = reduxtStore.authSlice?.login?.mail || "";
        const userId = reduxtStore.authSlice?.login?.id || "";
        return { userId, email };
    }
    /**
     * Fetch Account Onboarding Data for Search
     * Function signature matches SelectBoxWithSearch expectations
     */
    fetchAccountOnboardingData = async (
        filter: string,
        limit: number = 10,
        offset: number = 0,
        one_star_id: any[] = [],
        requested_info?: string,
        oneStarAccount?: string,
        searchText?: string
    ) => {
        try {
            const properties = 'id,name,accountnumber';

            let queryString = `properties=${properties}&limit=${limit}&offset=${offset}`;

            if (filter && filter.trim()) {
                queryString += `&filter=${encodeURIComponent(filter)}`;
            }

            const apiConfig = {
                method: 'get',
                url: `/v1/account?${queryString}`,
                customHeaders: {
                    requested_info: 'partner_merchant_onboarding_details_request'
                }
            };
            const response = await this.genericDatamartAPI(apiConfig);
            return {
                status: response.status,
                data: response.data || [],
            };
        } catch (error: any) {
            console.error("Error fetching account onboarding data:", error);
            return {
                status: false,
                message: error.message || 'Failed to fetch accounts',
                data: [],
                total_count: 0
            };
        }
    };

    /**
  * Fetch User Onboarding List - POST /user/all
  * ✅ Uses default properties if not provided
  */
    fetchUserOnboardingList = async (
        filter: string = "",
        limit: number = 30,
        offset: number = 0,
        properties: string[] = [],
        requestedInfo: string = "",
        xFactory: string = "dac",
        accountFilter?: { account_name?: string; status?: string }
    ) => {
        try {
            const { userId } = this.getState();

            // ✅ Use default properties if not provided
            const finalProperties = properties.length > 0 ? properties : this.DEFAULT_USER_PROPERTIES;

            const payload: any = {
                properties: finalProperties,
                filter,
                limit,
                offset
            };

            // ✅ Add account_filter if provided
            if (accountFilter && Object.keys(accountFilter).length > 0) {
                payload.account_filter = accountFilter;
            }

           

            const apiConfig = {
                method: 'post',
                url: `/user/all`,
                data: payload,
                customHeaders: {
                    'x-factory': xFactory,
                    'x-user-id': userId,
                    'operation': "read",
                    ...(requestedInfo && { 'requested_info': requestedInfo })
                }
            };

            const response = await this.genericDatamartAPI(apiConfig);

            return {
                status: response.status,
                data: response.data || [],
            };
        } catch (error: any) {
            console.error("Error fetching user onboarding list:", error);
            return {
                status: false,
                message: error.message || 'Failed to fetch user onboarding list',
                data: [],
                total_count: 0
            };
        }
    };

    /**
     * Add/Edit User - POST /user
     */
    addEditUser = async (
        userData: any,
        operation: string = "add",
        xFactory: string = "user"
    ) => {
        try {
            

            const apiConfig = {
                method: 'post',
                url: `/user?operation=${operation}`,
                data: userData,
                customHeaders: {
                    'x-factory': xFactory,
                    'x-user-id': this.getState().userId,
                    'operation': operation,
                    'requested_info': 'partner_merchant_onboarding_details_request'
                }
            };

            const response = await this.genericDatamartAPI(apiConfig);
           
            return {
                status: response.status,
                data: response.data || null,
                message: response.message || 'User saved successfully'
            };
        } catch (error: any) {
            console.error("Error add/edit user:", error);
            return {
                status: false,
                message: error.message || error.data?.response?.errorMessage || 'Failed to save user',
                data: null,
            };
        }
    };

    /**
     * Create Account Onboarding - POST /v1/account_onboarding
     * Used when account doesn't exist (no accountnumber/six_star_number)
     */
    createAccountOnboarding = async (payload: any) => {
        try {
           

            const apiConfig = {
                method: 'post',
                url: `/v1/account`,
                data: [payload], // API expects array
                customHeaders: {
                    'x-user-id': this.getState().userId,
                    'requested_info': 'merchant_account_onboarding',
                    'operation': 'add'
                }
            };

            const response = await this.genericDatamartAPI(apiConfig);

            return {
                status: response.status,
                data: response.data || null,
                message: response.message || 'Account onboarding created successfully'
            };
        } catch (error: any) {
            console.error("Error creating account onboarding:", error);
            return {
                status: false,
                message: error.message || error.data?.response?.errorMessage || 'Failed to create account onboarding',
                data: null,
            };
        }
    };
    /**
    * Update Account Onboarding - PATCH /v1/account_onboarding
    * Uses query params for accountnumber and/or six_star_number
    */
    updateAccountOnboarding = async (payload: any, accountNumber?: string, sixStarNumber?: string) => {
        try {
            

            // Build query params
            const queryParams = [];
            if (accountNumber) queryParams.push(`accountnumber=${accountNumber}`);
            if (sixStarNumber) queryParams.push(`six_star_number=${sixStarNumber}`);
            const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

            const apiConfig = {
                method: 'patch',
                url: `/v1/account${queryString}`,
                data: [payload],
                customHeaders: {
                    'x-user-id': this.getState().userId,
                    'requested_info': 'merchant_account_onboarding',
                    'operation': 'edit'
                }
            };

            const response = await this.genericDatamartAPI(apiConfig);

            return {
                status: response.status,
                data: response.data || null,
                message: response.message || 'Account onboarding updated successfully'
            };
        } catch (error: any) {
            console.error("Error updating account onboarding:", error);
            return {
                status: false,
                message: error.message || 'Failed to update account onboarding',
                data: null,
            };
        }
    };

    /**
     * Send User Onboarding Notification - POST /workflow/user-onboarding-notification
     */
    sendUserOnboardingNotification = async (
        notificationData: {
            user_name: string;
            user_email: string;
            link: string;
            comments?: string;
        },
        notifyHeader: string = 'user'
    ) => {
        try {
            

            const apiConfig = {
                method: 'post',
                url: `/workflow/user-onboarding-notification`,
                data: notificationData,
                customHeaders: {
                    'x-user-id': this.getState().userId,
                    'notify': notifyHeader // ✅ Dynamic notify header
                }
                // baseUrl: "http://localhost:4000"
            };

            const response = await this.genericDatamartAPI(apiConfig);

            return {
                status: response.status,
                data: response.data || null,
                message: response.message || 'Notification sent successfully'
            };
        } catch (error: any) {
            console.error("Error sending notification:", error);
            return {
                status: false,
                message: error.message || 'Failed to send notification',
                data: null,
            };
        }
    };


    /**
   * Submit Registration - Simple orchestration
   */
    submitRegistration = async (params: { formData: any; userId: string; }) => {
        const { formData, userId } = params;

        try {
            const state = store.getState();
            const loggedInUserDetails = state.common?.loggedInUserDetails;

            // Build account payload
            const accountPayload = {
                name: formData.companyName,
                website: formData.companyWebsite,
                vat_number: formData.companyVATNumber,
                industry: formData.companyIndustry,
                account_onboarding_status: "BASIC_INFO_SUBMITTED",
                other_info: {
                    billingaddress_country: formData.billingCountry,
                    billingaddress_street: formData.billingStreet,
                    billingaddress_city: formData.billingCity,
                    billingaddress_state: formData.billingState,
                    billingaddress_postalcode: formData.billingZipCode,
                    shippingcountry: formData.shippingCountry,
                    shippingstreet: formData.shippingStreet,
                    shippingcity: formData.shippingCity,
                    shippingstate: formData.shippingState,
                    shippingpostalcode: formData.shippingZipCode,
                }
            };

            // Decide: PATCH or POST
            let accountResponse;
            if (formData.accountNumber || formData.sixStarNumber) {
                // PATCH with query params
                accountResponse = await this.updateAccountOnboarding(
                    accountPayload,
                    formData.accountNumber,
                    formData.sixStarNumber
                );
            } else {
                // POST without query params
                accountResponse = await this.createAccountOnboarding(accountPayload);
            }

            if (!accountResponse.status) {
                return { status: false, message: 'Failed to save account onboarding', data: null };
            }

           // Extract six_star_id from account response
            const sixStarId = accountResponse.data?.id || accountResponse.data?.six_star_id;

            // Build role object
            const roleObject = {
                ...loggedInUserDetails,
                ...(!loggedInUserDetails?.phone_number && formData.phoneCode && formData.phoneNumber && {
                    phone_number: `${formData.phoneCode}-${formData.phoneNumber}`
                }),
                ...(!loggedInUserDetails?.contact_role && formData.contactRole && {
                    contact_role: formData.contactRole
                }),
                ...(sixStarId && { six_star: [sixStarId] })
            };
            // Update user
            const userResponse = await this.addEditUser({
                id: userId,
                fullName: `${formData.firstName} ${formData.lastName}`,
                email: formData.email,
                type: "user",
                active: 1,
                role: roleObject
            }, "edit", "user");

            if (!userResponse.status) {
                return { status: false, message: 'Failed to update user', data: null };
            }

            // // Send notification
            await this.sendUserOnboardingNotification({
                user_name: `${formData.firstName} ${formData.lastName}`,
                user_email: formData.email,
                link: `${window.location.origin}/user_onboarding`,
                comments: ""
            }, 'partners'); // ✅ Partner notification for registration

            return { status: true, message: 'Registration submitted successfully', data: userResponse.data };
        } catch (error: any) {
            console.error("Error submitting registration:", error);
            return { status: false, message: error.message || 'Registration failed', data: null };
        }
    };


    /**
   * Submit Action (Send Feedback / Review & Submit / Complete Onboarding)
   */
    submitAction = async (params: {
        accountData?: any;
        accountNumber?: string;
        sixStarNumber?: string;
        newStatus?: string;
        userName: string;
        userEmail: string;
        comments: string;
        action: 'feedback' | 'review' | 'complete';
        // ✅ Add params for user edit in review/complete
        userId?: string;
        oneStarNumber?: string;
        threeStarNumber?: string;
        userRole?: any;
    }) => {
        const {
            accountData,
            accountNumber,
            sixStarNumber,
            newStatus,
            userName,
            userEmail,
            comments,
            action,
            userId,
            oneStarNumber,
            threeStarNumber,
            userRole
        } = params;

        try {

            // ✅ For feedback, skip account update and only send notification
            if (action === 'feedback') {
                const notificationResponse = await this.sendUserOnboardingNotification(
                    {
                        user_name: userName,
                        user_email: userEmail,
                        link: `${window.location.origin}/user_onboarding`,
                        comments: comments || ''
                    },
                    'feedback_to_user'
                );

                return {
                    status: notificationResponse.status,
                    message: notificationResponse.message || 'Feedback sent successfully',
                    data: notificationResponse.data
                };
            }

            // ✅ For review/complete: Update account + update user + send notification
            const accountResponse = await this.updateAccountOnboarding(
                accountData,
                accountNumber,
                sixStarNumber
            );

            if (!accountResponse.status) {
                return accountResponse;
            }

            // ✅ Update user with one_star and three_star values
            if (userId && (oneStarNumber || threeStarNumber)) {
                const updatedRole = {
                    ...userRole,
                    ...(oneStarNumber && { one_star: [oneStarNumber] }),
                    ...(threeStarNumber && { three_star: [threeStarNumber] })
                };

                const userResponse = await this.addEditUser({
                    id: userId,
                    fullName: userName,
                    email: userEmail,
                    type: "user",
                    active: 1,
                    role: updatedRole
                }, "edit", "user");

                if (!userResponse.status) {
                    console.error("Failed to update user, but continuing...");
                }
            }

            // Determine notify header
            const notifyHeader = action === 'complete' ? 'user' : 'governors';

            // Send notification
            await this.sendUserOnboardingNotification(
                {
                    user_name: userName,
                    user_email: userEmail,
                    link: `${window.location.origin}/user_onboarding`,
                    comments: comments || ''
                },
                notifyHeader
            );

            return {
                status: true,
                message: 'Action completed successfully',
                data: accountResponse.data
            };

        } catch (error: any) {
            console.error("Error submitting action:", error);
            return {
                status: false,
                message: error.message || 'Failed to submit action',
                data: null,
            };
        }
    };
    /**
     * A generic function to make API calls (GET, POST, PUT, DELETE) to the Datamart service.
     */
    async genericDatamartAPI({
        method,
        url,
        data,
        customHeaders = {},
        baseUrl = process.env.REACT_APP_DATAMART
    }: {
        method: string;
        url: string;
        data?: any;
        customHeaders?: Record<string, string>;
        baseUrl?: string;
    }) {
        try {
            const { userId } = this.getState();

            const config: any = {
                method: method,
                url: `${baseUrl}${url}`,
                data: data,
                headers: {
                    'Content-Type': 'application/json',
                    "x-user-id": userId,
                    ...customHeaders
                }
            };

            const res = await axios(config);

            const response = {
                statusCode: res.status,
                statusText: res.statusText,
                status: res.data?.status,
                message: res.data?.message || res.data.errorMessage || '',
                data: res?.data?.data || [],
                requestId: res?.data?.requestId || '',
            };

            return response;
        } catch (err: any) {
            console.error("Error in genericDatamartAPI:", err);
            const errMesg = err.response?.data?.errorMessage
                ? err.response?.data?.errorMessage
                : err?.message;

            return {
                data: [],
                status: err?.response?.data?.status || false,
                message: errMesg,
                statusCode: err?.response?.status
            };
        }
    }
}

export const onboardingService = new OnboardingService();